import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Checkbox } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Chip } from '@thoughtspot/radiant-react/widgets/chip';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Menu } from '@thoughtspot/radiant-react/widgets/menu';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Radio } from '@thoughtspot/radiant-react/widgets/radio';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getGuidsToSkip } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useGetGroupsPrincipalListQuery } from "../../../../../services/user-group/user-group-service";
import { getStatusFilterLabel, getStatusFilterOptions, isUserStatusSupported, UserFilterKey, V2TranslationKeys, } from '../constants';
import styles from './UsersFilterBar.container.module.scss';
const GROUPS_FILTER_BATCH_SIZE = 50;
const GROUPS_SEARCH_DEBOUNCE_MS = 250;
const GROUPS_SCROLL_THRESHOLD_PX = 80;
const useDebouncedValue = (value, delayMs) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
};
const GroupsValuesList = ({ selectedIds, cachedLabels, onSelectionChange, onOptionsLoaded, }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, GROUPS_SEARCH_DEBOUNCE_MS);
    const [fetchKey, setFetchKey] = useState({ pattern: '', offset: 0 });
    const [pages, setPages] = useState([]);
    const [isLastBatch, setIsLastBatch] = useState(false);
    const appendedKeysRef = useRef(new Set());
    const isPagingRef = useRef(false);
    useEffect(() => {
        if (fetchKey.pattern !== debouncedSearch) {
            setFetchKey({ pattern: debouncedSearch, offset: 0 });
            setPages([]);
            setIsLastBatch(false);
            appendedKeysRef.current = new Set();
            isPagingRef.current = false;
        }
    }, [debouncedSearch, fetchKey.pattern]);
    const { data, loading } = useGetGroupsPrincipalListQuery({
        pattern: fetchKey.pattern,
        skipIds: getGuidsToSkip(),
        batchSize: GROUPS_FILTER_BATCH_SIZE,
        offset: fetchKey.offset,
    });
    useEffect(() => {
        if (loading || !data)
            return;
        const key = `${fetchKey.pattern}|${fetchKey.offset}`;
        if (appendedKeysRef.current.has(key))
            return;
        appendedKeysRef.current.add(key);
        const page = (data.objects ?? [])
            .filter((g) => g != null)
            .map(g => ({
            id: g.id ?? '',
            name: g.displayName || g.name || '',
        }))
            .filter(o => o.id);
        setPages(prev => (fetchKey.offset === 0 ? [page] : [...prev, page]));
        setIsLastBatch(Boolean(data.isLastBatch));
        isPagingRef.current = false;
        if (page.length)
            onOptionsLoaded(page);
    }, [data, loading, fetchKey, onOptionsLoaded]);
    const groupOptions = useMemo(() => {
        const seen = new Set();
        return pages.flat().filter(opt => {
            if (seen.has(opt.id))
                return false;
            seen.add(opt.id);
            return true;
        });
    }, [pages]);
    const items = useMemo(() => {
        const selectedMissing = selectedIds
            .filter(id => !groupOptions.some(o => o.id === id))
            .map(id => ({ id, name: cachedLabels[id] ?? id }));
        return [...selectedMissing, ...groupOptions];
    }, [groupOptions, selectedIds, cachedLabels]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const toggleId = (id) => {
        onSelectionChange(selectedSet.has(id)
            ? selectedIds.filter(g => g !== id)
            : [...selectedIds, id]);
    };
    const listRef = useRef(null);
    const handleScroll = () => {
        const el = listRef.current;
        if (!el || loading || isLastBatch || isPagingRef.current)
            return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight <
            GROUPS_SCROLL_THRESHOLD_PX) {
            isPagingRef.current = true;
            setFetchKey(prev => ({
                ...prev,
                offset: prev.offset + GROUPS_FILTER_BATCH_SIZE,
            }));
        }
    };
    const renderBody = () => {
        if (!items.length && !loading) {
            return (React.createElement(Typography, { variant: "p-small", className: styles.valuesEmpty }, t(V2TranslationKeys.NO_MATCH)));
        }
        return (React.createElement(React.Fragment, null,
            items.map(opt => (React.createElement(Checkbox, { key: opt.id, id: `groups-filter-${opt.id}`, checked: selectedSet.has(opt.id), onChange: () => toggleId(opt.id), label: opt.name, className: styles.valuesItem, checkboxControlClassName: styles.checkboxControl }))),
            loading && (React.createElement(Typography, { variant: "p-small", className: styles.valuesEmpty }, t(V2TranslationKeys.LOADING)))));
    };
    return (React.createElement(Vertical, { className: styles.valuesPopover },
        React.createElement(Vertical, { className: styles.searchBarWrap },
            React.createElement(SearchBar, { searchValue: search, searchPlaceholder: t(V2TranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: setSearch, "data-testid": "users-filter-groups-search", className: styles.searchBar })),
        React.createElement(Divider, { className: styles.divider }),
        React.createElement("div", { ref: listRef, onScroll: handleScroll, className: styles.valuesList }, renderBody()),
        selectedIds.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Divider, { className: styles.divider }),
            React.createElement(Horizontal, { className: styles.valuesFooter },
                React.createElement(Button, { type: "tertiary", text: t(V2TranslationKeys.CLEAR_SELECTION), onClick: () => onSelectionChange([]) }))))));
};
const getGroupsChipValue = (selectedIds, knownGroups) => {
    if (!selectedIds.length)
        return '';
    const firstName = knownGroups[selectedIds[0]] ?? selectedIds[0];
    const extra = selectedIds.length - 1;
    return extra > 0 ? `${firstName} +${extra}` : firstName;
};
const OrgsValuesList = ({ selectedIds, options, loading, onSelectionChange, }) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        if (!search.trim())
            return options;
        const lower = search.toLowerCase();
        return options.filter(o => o.name.toLowerCase().includes(lower));
    }, [options, search]);
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const toggleId = (id) => {
        onSelectionChange(selectedSet.has(id)
            ? selectedIds.filter(o => o !== id)
            : [...selectedIds, id]);
    };
    const renderBody = () => {
        if (loading) {
            return (React.createElement(Typography, { variant: "p-small", className: styles.valuesEmpty }, t(V2TranslationKeys.LOADING)));
        }
        if (!filtered.length) {
            return (React.createElement(Typography, { variant: "p-small", className: styles.valuesEmpty }, t(V2TranslationKeys.NO_MATCH)));
        }
        return filtered.map(opt => (React.createElement(Checkbox, { key: opt.id, id: `orgs-filter-${opt.id}`, checked: selectedSet.has(opt.id), onChange: () => toggleId(opt.id), label: opt.name, className: styles.valuesItem })));
    };
    return (React.createElement(Vertical, { className: styles.valuesPopover },
        React.createElement(Vertical, { className: styles.searchBarWrap },
            React.createElement(SearchBar, { searchValue: search, searchPlaceholder: t(V2TranslationKeys.SEARCH_PLACEHOLDER), onSearchChange: setSearch, "data-testid": "users-filter-orgs-search" })),
        React.createElement(Divider, { className: styles.divider }),
        React.createElement("div", { className: styles.valuesList }, renderBody()),
        selectedIds.length > 0 && (React.createElement(React.Fragment, null,
            React.createElement(Divider, { className: styles.divider }),
            React.createElement(Horizontal, { className: styles.valuesFooter },
                React.createElement(Button, { type: "tertiary", text: t(V2TranslationKeys.CLEAR_SELECTION), onClick: () => onSelectionChange([]) }))))));
};
export const UsersFilterBar = ({ groupIds, accountStatus = null, onGroupsChange, onStatusChange, onClearAll, orgIds = [], onOrgsChange, orgOptions = [], orgsLoading = false, availableDimensions, isDisabled = false, }) => {
    const { t } = useTranslation();
    const isDimensionEnabled = (key) => !availableDimensions || availableDimensions.includes(key);
    const isStatusEnabled = isDimensionEnabled(UserFilterKey.STATUS) &&
        Boolean(onStatusChange) &&
        isUserStatusSupported();
    const isOrgsEnabled = isDimensionEnabled(UserFilterKey.ORGS) && Boolean(onOrgsChange);
    const isGroupsEnabled = isDimensionEnabled(UserFilterKey.GROUPS);
    const [addedDimensions, setAddedDimensions] = useState(() => {
        const init = new Set();
        if (isGroupsEnabled && groupIds.length) {
            init.add(UserFilterKey.GROUPS);
        }
        if (isOrgsEnabled && orgIds.length) {
            init.add(UserFilterKey.ORGS);
        }
        if (isStatusEnabled && accountStatus) {
            init.add(UserFilterKey.STATUS);
        }
        return init;
    });
    const [openDimensionPicker, setOpenDimensionPicker] = useState(false);
    const [openValuesFor, setOpenValuesFor] = useState(null);
    const [groupLabelCache, setGroupLabelCache] = useState({});
    const addFilterButtonRef = useRef(null);
    const groupsChipAnchorRef = useRef(null);
    const orgsChipAnchorRef = useRef(null);
    const statusChipAnchorRef = useRef(null);
    const orgNameById = useMemo(() => {
        const map = {};
        orgOptions.forEach(o => {
            map[o.id] = o.name;
        });
        return map;
    }, [orgOptions]);
    const cacheGroupLabels = useCallback((options) => {
        setGroupLabelCache(prev => {
            const next = { ...prev };
            let changed = false;
            options.forEach(opt => {
                if (next[opt.id] !== opt.name) {
                    next[opt.id] = opt.name;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, []);
    const anyFilterActive = (isGroupsEnabled && groupIds.length > 0) ||
        (isOrgsEnabled && orgIds.length > 0) ||
        (isStatusEnabled && accountStatus !== null);
    const addDimension = (key) => {
        setAddedDimensions(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
        setOpenDimensionPicker(false);
        setTimeout(() => setOpenValuesFor(key), 0);
    };
    const handleClearAll = () => {
        setAddedDimensions(new Set());
        setOpenValuesFor(null);
        setOpenDimensionPicker(false);
        onClearAll();
    };
    const closeValuesPopover = () => {
        setOpenValuesFor(null);
    };
    const removeDimension = (key) => {
        setAddedDimensions(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        if (key === UserFilterKey.GROUPS && groupIds.length > 0) {
            onGroupsChange([]);
        }
        if (key === UserFilterKey.ORGS && orgIds.length > 0 && onOrgsChange) {
            onOrgsChange([]);
        }
        if (key === UserFilterKey.STATUS &&
            accountStatus !== null &&
            onStatusChange) {
            onStatusChange(null);
        }
        if (openValuesFor === key)
            setOpenValuesFor(null);
    };
    const onStatusRadioChange = (event) => {
        const target = event.target;
        onStatusChange?.(target.value);
    };
    const toggleValuesPopover = (key) => {
        setOpenValuesFor(prev => (prev === key ? null : key));
    };
    const dimensionOptions = [
        ...(isGroupsEnabled
            ? [
                {
                    key: UserFilterKey.GROUPS,
                    label: t(V2TranslationKeys.COL_GROUPS),
                },
            ]
            : []),
        ...(isOrgsEnabled
            ? [
                {
                    key: UserFilterKey.ORGS,
                    label: t(V2TranslationKeys.COL_ORGS),
                },
            ]
            : []),
        ...(isStatusEnabled
            ? [
                {
                    key: UserFilterKey.STATUS,
                    label: t(V2TranslationKeys.COL_STATUS),
                },
            ]
            : []),
    ];
    const renderGroupsChip = () => {
        if (!addedDimensions.has(UserFilterKey.GROUPS))
            return null;
        const valueText = getGroupsChipValue(groupIds, groupLabelCache) ||
            t(V2TranslationKeys.SELECT);
        const fullValueList = groupIds.length > 1
            ? groupIds.map(id => groupLabelCache[id] ?? id).join(', ')
            : undefined;
        return (React.createElement("span", { ref: groupsChipAnchorRef, className: styles.chipAnchor, "data-testid": "users-filter-chip-groups", title: fullValueList },
            React.createElement(Chip.Trigger, { type: "filter", content: [
                    {
                        text: t(V2TranslationKeys.COL_GROUPS),
                        style: 'regular',
                    },
                    { text: valueText, style: 'bold' },
                ], onClick: () => toggleValuesPopover(UserFilterKey.GROUPS), onClose: () => removeDimension(UserFilterKey.GROUPS) })));
    };
    const renderOrgsChip = () => {
        if (!addedDimensions.has(UserFilterKey.ORGS))
            return null;
        const valueText = getGroupsChipValue(orgIds, orgNameById) ||
            t(V2TranslationKeys.SELECT);
        const fullValueList = orgIds.length > 1
            ? orgIds.map(id => orgNameById[id] ?? id).join(', ')
            : undefined;
        return (React.createElement("span", { ref: orgsChipAnchorRef, className: styles.chipAnchor, "data-testid": "users-filter-chip-orgs", title: fullValueList },
            React.createElement(Chip.Trigger, { type: "filter", content: [
                    {
                        text: t(V2TranslationKeys.COL_ORGS),
                        style: 'regular',
                    },
                    { text: valueText, style: 'bold' },
                ], onClick: () => toggleValuesPopover(UserFilterKey.ORGS), onClose: () => removeDimension(UserFilterKey.ORGS) })));
    };
    const renderStatusChip = () => {
        if (!addedDimensions.has(UserFilterKey.STATUS))
            return null;
        const valueText = accountStatus
            ? getStatusFilterLabel(accountStatus)
            : t(V2TranslationKeys.SELECT);
        return (React.createElement("span", { ref: statusChipAnchorRef, className: styles.chipAnchor, "data-testid": "users-filter-chip-status" },
            React.createElement(Chip.Trigger, { type: "filter", content: [
                    {
                        text: t(V2TranslationKeys.COL_STATUS),
                        style: 'regular',
                    },
                    { text: valueText, style: 'bold' },
                ], onClick: () => toggleValuesPopover(UserFilterKey.STATUS), onClose: () => removeDimension(UserFilterKey.STATUS) })));
    };
    const showAddButton = addedDimensions.size < dimensionOptions.length;
    return (React.createElement(Horizontal, { vAlignContent: "center", spacing: "b", wrap: true },
        isGroupsEnabled && renderGroupsChip(),
        isOrgsEnabled && renderOrgsChip(),
        isStatusEnabled && renderStatusChip(),
        showAddButton && (React.createElement(Button, { buttonRef: addFilterButtonRef, icon: IconID.FUNNEL, type: "tertiary", text: t(V2TranslationKeys.ADD_FILTER), onClick: () => setOpenDimensionPicker(v => !v), isDisabled: isDisabled, "data-testid": "users-add-filter" })),
        anyFilterActive && (React.createElement(Button, { type: "tertiary", text: t(V2TranslationKeys.CLEAR_ALL), onClick: handleClearAll, "data-testid": "users-clear-all-filters" })),
        React.createElement(Popover, { align: "bottom-start", bottomOffset: 10, targetRef: addFilterButtonRef, isVisible: openDimensionPicker, onClickOutside: () => setOpenDimensionPicker(false), content: React.createElement(Menu, { show: true, className: styles.dimensionPopover }, dimensionOptions.map(opt => (React.createElement(Menu.Item, { key: opt.key, id: `users-filter-dim-${opt.key}`, label: opt.label, disabled: addedDimensions.has(opt.key), onSelect: () => addDimension(opt.key) })))) }),
        isGroupsEnabled && (React.createElement(Popover, { align: "bottom-start", bottomOffset: 10, targetRef: groupsChipAnchorRef, isVisible: openValuesFor === UserFilterKey.GROUPS, onClickOutside: closeValuesPopover, content: openValuesFor === UserFilterKey.GROUPS ? (React.createElement(GroupsValuesList, { selectedIds: groupIds, cachedLabels: groupLabelCache, onSelectionChange: onGroupsChange, onOptionsLoaded: cacheGroupLabels })) : null })),
        isOrgsEnabled && onOrgsChange && (React.createElement(Popover, { align: "bottom-start", bottomOffset: 10, targetRef: orgsChipAnchorRef, isVisible: openValuesFor === UserFilterKey.ORGS, onClickOutside: closeValuesPopover, content: openValuesFor === UserFilterKey.ORGS ? (React.createElement(OrgsValuesList, { selectedIds: orgIds, options: orgOptions, loading: orgsLoading, onSelectionChange: onOrgsChange })) : null })),
        isStatusEnabled && (React.createElement(Popover, { align: "bottom-start", bottomOffset: 12, targetRef: statusChipAnchorRef, isVisible: openValuesFor === UserFilterKey.STATUS, onClickOutside: closeValuesPopover, content: React.createElement(Vertical, { className: styles.valuesPopover },
                React.createElement(Vertical, { className: styles.valuesList },
                    React.createElement(Radio.Group, { selectedValue: accountStatus ?? undefined, onChange: onStatusRadioChange, orientation: "list", legendContent: t(V2TranslationKeys.COL_STATUS) }, getStatusFilterOptions().map(status => (React.createElement(Radio, { key: status, value: status, label: getStatusFilterLabel(status), isSideLabel: true, className: styles.valuesItem }))))),
                accountStatus && (React.createElement(React.Fragment, null,
                    React.createElement(Divider, { className: styles.divider }),
                    React.createElement(Horizontal, { className: styles.valuesFooter },
                        React.createElement(Button, { type: "tertiary", text: t(V2TranslationKeys.CLEAR_SELECTION), onClick: () => onStatusChange?.(null) }))))) }))));
};
//# sourceMappingURL=UsersFilterBar.container.js.map